import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isUndeliveredFilterRefused,
  UNDELIVERED_COUNT_REFRESH_MS,
} from '../MessengerV2/Messenger.functions';
import { MessengerService } from '../MessengerV2/Messenger.service';
import { tBoardFilterParams } from './Pipe.types';

type tBoardUndeliveredCountOptions = {
  enabled: boolean;
  workspaceId: string | null | undefined;
  funnelId: string | null | undefined;
  filterParams: tBoardFilterParams;
};

type tBoardUndeliveredCount = {
  /** N on the chip; null hides it. */
  count: number | null;
  /**
   * Ask for the count again, at most once per UNDELIVERED_COUNT_REFRESH_MS.
   * The board calls it from its own lead socket handlers.
   */
  scheduleRefresh: () => void;
};

type tCountState = {
  key: string | null;
  count: number | null;
  unsupported: boolean;
};

const EMPTY_STATE: tCountState = { key: null, count: null, unsupported: false };

/**
 * INT-1596 / INT-1597: N on the funnel board's "No entregados (N)" chip.
 *
 * The funnel's classified leads only (`inbox=false`, D8), under the filters
 * the columns send, so N is what the board lists with the chip on. Asked again
 * when the funnel or a filter changes and, throttled, whenever the board's lead
 * socket handlers call `scheduleRefresh`, so the number drops without a refresh
 * when someone else resolves a lead (D5).
 *
 * Null hides the chip: a count that could not be read is never shown as a
 * zero, and a workspace that refused the filter (422, projection-backed) keeps
 * it hidden, as the chat list does.
 */
export function useBoardUndeliveredCount(
  options: tBoardUndeliveredCountOptions,
): tBoardUndeliveredCount {
  const { enabled, workspaceId, funnelId, filterParams } = options;
  const [state, setState] = useState<tCountState>(EMPTY_STATE);
  const stateRef = useRef<tCountState>(EMPTY_STATE);
  const refreshRef = useRef<() => void>();
  const throttleRef = useRef<ReturnType<typeof setTimeout>>();

  const isActive = enabled && !!workspaceId && !!funnelId;
  // Stable across renders that rebuild the same filters, so an unrelated
  // re-render never asks for the count again.
  const filterKey = JSON.stringify(filterParams);
  const requestKey = isActive
    ? `${workspaceId}:${funnelId}:${filterKey}`
    : null;

  const scheduleRefresh = useCallback(() => {
    if (!isActive || throttleRef.current) return;
    throttleRef.current = setTimeout(() => {
      throttleRef.current = undefined;
      refreshRef.current?.();
    }, UNDELIVERED_COUNT_REFRESH_MS);
  }, [isActive]);

  useEffect(() => () => clearTimeout(throttleRef.current), []);

  useEffect(() => {
    if (!enabled || !workspaceId || !funnelId || !requestKey) return undefined;
    let isCurrent = true;
    let requestId = 0;
    const publish = (next: tCountState) => {
      const previous = stateRef.current;
      if (
        previous.key === next.key &&
        previous.count === next.count &&
        previous.unsupported === next.unsupported
      ) return;
      stateRef.current = next;
      setState(next);
    };
    const refresh = () => {
      const currentRequestId = ++requestId;
      MessengerService.getUndeliveredCount(workspaceId, {
        ...(JSON.parse(filterKey) as tBoardFilterParams),
        funnelId,
        active: true,
        inbox: false,
      })
        .then((count) => {
          if (!isCurrent || currentRequestId !== requestId) return;
          if (
            stateRef.current.key === requestKey && stateRef.current.unsupported
          ) return;
          publish({ key: requestKey, count, unsupported: false });
        })
        .catch((error) => {
          if (!isCurrent || currentRequestId !== requestId) return;
          const unsupported = isUndeliveredFilterRefused(error);
          publish({ key: requestKey, count: null, unsupported });
        });
    };
    refreshRef.current = refresh;
    refresh();

    return () => {
      isCurrent = false;
      refreshRef.current = undefined;
    };
    // `filterKey` and `requestKey` stand in for the objects they serialise.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: the serialized request key owns filter changes.
  }, [requestKey]);

  const count =
    isActive && state.key === requestKey && !state.unsupported
      ? state.count
      : null;

  return { count, scheduleRefresh };
}
